// deno-lint-ignore-file no-explicit-any

/**
 * Reconnecting WebSocket with automatic retry logic.
 *
 * Features:
 * - **Drop-in replacement** — standard `WebSocket` API, swap one line
 * - **Auto-reconnection** — configurable retries and delay strategy
 * - **Message buffering** — `send()` queues data while offline, flushes on reconnect
 * - **Persistent listeners** — `addEventListener` and `on*` handlers survive reconnections
 * - **Dynamic URL & protocols** — resolve fresh values on each reconnection
 * - **Zero dependencies** — works in Node.js, Deno, Bun, and browsers
 *
 * @example
 * ```ts
 * import { ReconnectingWebSocket } from "@nktkas/rews";
 *
 * const ws = new ReconnectingWebSocket("wss://api.example.com/ws", {
 *   maxRetries: 10,
 *   reconnectionDelay: (attempt) => Math.min(2 ** attempt * 100, 5_000),
 * });
 *
 * ws.addEventListener("open", () => console.log("connected")); // fires on every reconnect
 * ws.addEventListener("message", (event) => console.log(event.data));
 *
 * ws.send("hello"); // buffered while offline, sent once connected
 * ```
 *
 * @module
 */

// =============================================================================
// Types
// =============================================================================

// --- WebSocket ---------------------------------------------------------------

/**
 * Specifies the type of binary data being received over a `WebSocket` connection.
 *
 * - `"blob"`: Binary data is returned as `Blob` objects
 * - `"arraybuffer"`: Binary data is returned as `ArrayBuffer` objects
 *
 * @example
 * ```ts
 * // Setting up WebSocket for binary data as ArrayBuffer
 * const ws = new WebSocket("ws://localhost:8080");
 * ws.binaryType = "arraybuffer";
 *
 * ws.onmessage = (event) => {
 *   if (event.data instanceof ArrayBuffer) {
 *     // Process binary data
 *     const view = new Uint8Array(event.data);
 *     console.log(`Received binary data of ${view.length} bytes`);
 *   } else {
 *     // Process text data
 *     console.log(`Received text: ${event.data}`);
 *   }
 * };
 *
 * // Sending binary data
 * const binaryData = new Uint8Array([1, 2, 3, 4]);
 * ws.send(binaryData.buffer);
 * ```
 *
 * @see https://developer.mozilla.org/en-US/docs/Web/API/WebSocket/binaryType
 * @category WebSockets
 */
export type BinaryType = "blob" | "arraybuffer";

/**
 * Interface mapping `WebSocket` event names to their corresponding event types.
 * Used for strongly typed event handling with `addEventListener` and `removeEventListener`.
 *
 * @example
 * ```ts
 * // Using with TypeScript for strongly-typed event handling
 * const ws = new WebSocket("ws://localhost:8080");
 *
 * ws.addEventListener("open", (event) => {
 *   console.log("Connection established");
 * });
 *
 * ws.addEventListener("message", (event: MessageEvent) => {
 *   console.log(`Received: ${event.data}`);
 * });
 * ```
 *
 * @category WebSockets
 */
export interface WebSocketEventMap {
  /** Connection closed. */
  close: CloseEvent;
  /** Connection failed. */
  error: Event;
  /** Data received. */
  message: MessageEvent;
  /** Connection established. */
  open: Event;
}

/**
 * Callback function invoked when an event fires.
 *
 * @category Events
 */
export interface EventListener {
  /**
   * The `EventListener` interface represents a callback function to be called
   * whenever an event of a specific type occurs on a target object.
   *
   * This is a basic event listener, represented by a simple function
   * that receives an Event object as its only parameter.
   *
   * @example
   * ```ts
   * // Create an event listener function
   * const handleEvent = (event: Event) => {
   *   console.log(`Event of type "${event.type}" occurred`);
   *   console.log(`Event phase: ${event.eventPhase}`);
   *
   *   // Access event properties
   *   if (event.cancelable) {
   *     event.preventDefault();
   *   }
   * };
   *
   * // Attach the event listener to a target
   * const target = new EventTarget();
   * target.addEventListener('custom', handleEvent);
   *
   * // Or create a listener inline
   * target.addEventListener('message', (event) => {
   *   console.log('Message received:', event);
   * });
   * ```
   *
   * @category Events
   */
  (evt: Event): void;
}

/**
 * The `EventListenerObject` interface represents an object that can handle events
 * dispatched by an `EventTarget` object.
 *
 * This interface provides an alternative to using a function as an event listener.
 * When implementing an object with this interface, the `handleEvent()` method
 * will be called when the event is triggered.
 *
 * @example
 * ```ts
 * // Creating an object that implements `EventListenerObject`
 * const myEventListener = {
 *   handleEvent(event: Event) {
 *     console.log(`Event of type ${event.type} occurred`);
 *
 *     // You can use 'this' to access other methods or properties
 *     this.additionalProcessing(event);
 *   },
 *
 *   additionalProcessing(event: Event) {
 *     // Additional event handling logic
 *     console.log('Additional processing for:', event);
 *   }
 * };
 *
 * // Using with any EventTarget (server or client contexts)
 * const target = new EventTarget();
 * target.addEventListener('message', myEventListener);
 *
 * // Later, to remove it:
 * target.removeEventListener('message', myEventListener);
 * ```
 *
 * @category Events
 */
export interface EventListenerObject {
  /** Called when a subscribed event fires. */
  handleEvent(evt: Event): void;
}

/**
 * Event listener in either callback or object form.
 *
 * @category Events
 */
export type EventListenerOrEventListenerObject =
  | EventListener
  | EventListenerObject;

// --- ReconnectingWebSocket ---------------------------------------------------

/** URL or factory function that returns a URL for the WebSocket connection (sync or async). */
export type UrlProvider =
  | string
  | URL
  | (() => string | URL | Promise<string | URL>);
/** Subprotocol(s) or factory function that returns subprotocol(s) for the WebSocket connection (sync or async). */
export type ProtocolsProvider =
  | string
  | string[]
  | undefined
  | (() => string | string[] | undefined | Promise<string | string[] | undefined>);

/** Error code indicating the type of reconnection failure. */
export type ReconnectingWebSocketErrorCode =
  | "RECONNECTION_LIMIT"
  | "RECONNECTION_DECLINED"
  | "TERMINATED_BY_USER"
  | "UNKNOWN_ERROR";

/** Configuration options for the {@link ReconnectingWebSocket}. */
export interface ReconnectingWebSocketOptions {
  /**
   * Maximum number of consecutive failed reconnection attempts.
   *
   * The counter resets after a connection stays open for {@link stableTimeout}.
   *
   * @default `Infinity`
   */
  maxRetries?: number;
  /**
   * Maximum number of messages buffered while disconnected; sends beyond the limit are silently dropped.
   *
   * @default `Infinity`
   */
  maxEnqueuedMessages?: number;
  /**
   * Maximum time in ms to wait for a connection to open. Set to `null` to disable.
   *
   * Does not limit the time spent in url/protocols factories.
   *
   * @default `10_000`
   */
  connectionTimeout?: number | null;
  /**
   * Time in ms a connection must stay open before the retry counter resets.
   *
   * @default `3_000`
   */
  stableTimeout?: number;
  /**
   * Delay before reconnection in ms, or a function of the attempt number (0-based).
   *
   * @default Exponential backoff `2 ** n * 150` capped at 10s, with equal jitter.
   */
  reconnectionDelay?: number | ((attempt: number) => number);
  /**
   * Decide whether to reconnect after a non-user closure. Return `false` to permanently terminate.
   *
   * Receives the close event and the attempt number (0-based).
   * Not consulted for `close()` and `reconnect()` calls.
   *
   * @default `() => true`
   */
  shouldReconnect?: (event: CloseEvent, attempt: number) => boolean;
}

// =============================================================================
// Classes
// =============================================================================

/**
 * Error thrown when reconnection fails in {@link ReconnectingWebSocket}.
 *
 * @example
 * ```ts
 * import { ReconnectingWebSocket, ReconnectingWebSocketError } from "@nktkas/rews";
 *
 * const ws = new ReconnectingWebSocket("wss://api.example.com/ws");
 *
 * ws.terminationSignal.aborted; // boolean
 * ws.terminationSignal.reason; // ReconnectingWebSocketError, once aborted
 *
 * ws.terminationSignal.addEventListener("abort", () => {
 *   ws.terminationSignal.reason.code; // ReconnectingWebSocketErrorCode
 *   ws.terminationSignal.reason.cause; // original error, if any
 * });
 * ```
 */
export class ReconnectingWebSocketError extends Error {
  /**
   * Error code indicating the type of reconnection error:
   * - `RECONNECTION_LIMIT`: Maximum reconnection attempts reached.
   * - `RECONNECTION_DECLINED`: `shouldReconnect` returned `false`.
   * - `TERMINATED_BY_USER`: Closed via `close()` method.
   * - `UNKNOWN_ERROR`: Unhandled error outside a connection attempt (e.g. in `reconnectionDelay` or `shouldReconnect`).
   */
  readonly code: ReconnectingWebSocketErrorCode;

  /**
   * Create a termination error.
   *
   * @param code Reconnection failure code.
   * @param cause Underlying error, stored as [`Error#cause`](https://developer.mozilla.org/en-US/docs/Web/JavaScript/Reference/Global_Objects/Error/cause).
   */
  constructor(code: ReconnectingWebSocketErrorCode, cause?: unknown) {
    super(`WebSocket permanently terminated: ${code}`, { cause });
    this.name = "ReconnectingWebSocketError";
    this.code = code;
  }
}

export interface ReconnectingWebSocket {
  addEventListener<K extends keyof WebSocketEventMap>(
    type: K,
    listener: (this: ReconnectingWebSocket, ev: WebSocketEventMap[K]) => any,
    options?: boolean | AddEventListenerOptions,
  ): void;
  addEventListener(
    type: string,
    listener: EventListenerOrEventListenerObject,
    options?: boolean | AddEventListenerOptions,
  ): void;
  removeEventListener<K extends keyof WebSocketEventMap>(
    type: K,
    listener: (this: ReconnectingWebSocket, ev: WebSocketEventMap[K]) => any,
    options?: boolean | EventListenerOptions,
  ): void;
  removeEventListener(
    type: string,
    listener: EventListenerOrEventListenerObject,
    options?: boolean | EventListenerOptions,
  ): void;
}

/**
 * Drop-in replacement for [`WebSocket`](https://developer.mozilla.org/en-US/docs/Web/API/WebSocket) that automatically reconnects.
 *
 * @example
 * ```ts
 * import { ReconnectingWebSocket } from "@nktkas/rews";
 *
 * const ws = new ReconnectingWebSocket("wss://api.example.com/ws", {
 *   maxRetries: 10,
 *   reconnectionDelay: (attempt) => Math.min(2 ** attempt * 100, 5_000),
 * });
 *
 * ws.addEventListener("open", () => console.log("connected")); // fires on every reconnect
 * ws.addEventListener("message", (event) => console.log(event.data));
 *
 * ws.send("hello"); // buffered while offline, sent once connected
 * ```
 */
export class ReconnectingWebSocket extends EventTarget implements WebSocket {
  // ===========================================================================
  // State and initialization
  // ===========================================================================

  // --- Private state ---------------------------------------------------------

  /** URL provider for creating new connections. */
  private readonly _urlProvider: UrlProvider;
  /** Protocols provider for creating new connections. */
  private readonly _protocolsProvider: ProtocolsProvider;

  /** Current underlying WebSocket instance. */
  private _socket: WebSocket | undefined;
  /** Binary data type for the WebSocket. */
  private _binaryType: BinaryType = "blob";
  /** Buffer for messages sent while disconnected. */
  private _messageBuffer: (string | ArrayBufferLike | Blob | ArrayBufferView)[] = [];

  /** Current reconnection attempt number. */
  private _retryCount = 0;
  /** Resolves the in-flight {@link _awaitSocketLifecycle} promise; `undefined` when idle. */
  private _settleLifecycle: ((init: CloseEventInit) => void) | undefined;
  /** Resolves the in-flight retry delay; `undefined` when not sleeping. */
  private _skipDelay: (() => void) | undefined;
  /** Set while a user-requested reconnect closes the current socket; that close is not counted as a retry. */
  private _reconnectRequested = false;
  /** Skips the upcoming retry delay; set by {@link reconnect} when no sleep is active. */
  private _skipNextDelay = false;

  /** Attribute-style listener for the `close` event. */
  private _onclose: ((this: WebSocket, ev: CloseEvent) => any) | null = null;
  /** Attribute-style listener for the `error` event. */
  private _onerror: ((this: WebSocket, ev: Event) => any) | null = null;
  /** Attribute-style listener for the `message` event. */
  private _onmessage: ((this: WebSocket, ev: MessageEvent) => any) | null = null;
  /** Attribute-style listener for the `open` event. */
  private _onopen: ((this: WebSocket, ev: Event) => any) | null = null;
  /** Map of currently active attribute-style listeners. */
  private _attributeListeners: Partial<Record<"open" | "close" | "error" | "message", EventListener>> = {};

  /** Controller used to signal permanent termination. */
  private readonly _abortController: AbortController = new AbortController();

  // --- Public state ----------------------------------------------------------

  /** Reconnection configuration options. Read on every attempt, so changes apply live. */
  reconnectOptions: Required<ReconnectingWebSocketOptions>;

  /**
   * AbortSignal that is aborted when the instance is permanently terminated.
   * The abort reason is always a {@link ReconnectingWebSocketError}.
   */
  get terminationSignal(): AbortSignal {
    return this._abortController.signal;
  }

  // --- Constructor -----------------------------------------------------------

  /**
   * Create a new ReconnectingWebSocket with URL and options.
   *
   * @param url URL or factory function for the WebSocket connection.
   * @param options Configuration options.
   *
   * @throws {TypeError} If no WebSocket implementation is available.
   */
  constructor(url: UrlProvider, options?: ReconnectingWebSocketOptions);
  /**
   * Create a new ReconnectingWebSocket with URL, protocols and options.
   *
   * @param url URL or factory function for the WebSocket connection.
   * @param protocols Subprotocol(s) or factory function.
   * @param options Configuration options.
   *
   * @throws {TypeError} If no WebSocket implementation is available.
   */
  constructor(url: UrlProvider, protocols?: ProtocolsProvider, options?: ReconnectingWebSocketOptions);
  constructor(
    url: UrlProvider,
    protocolsOrOptions?: ProtocolsProvider | ReconnectingWebSocketOptions,
    maybeOptions?: ReconnectingWebSocketOptions,
  ) {
    super();

    const secondArgIsProtocols = protocolsOrOptions === undefined ||
      typeof protocolsOrOptions === "string" ||
      typeof protocolsOrOptions === "function" ||
      Array.isArray(protocolsOrOptions);
    const protocols = secondArgIsProtocols ? protocolsOrOptions : undefined;
    const options = secondArgIsProtocols ? maybeOptions : protocolsOrOptions;

    // Without this guard, a runtime lacking WebSocket retries forever instead of failing fast.
    if (!globalThis.WebSocket) {
      throw new TypeError("No WebSocket implementation found.");
    }

    this._urlProvider = url;
    this._protocolsProvider = protocols;
    this.reconnectOptions = {
      maxRetries: options?.maxRetries ?? Infinity,
      maxEnqueuedMessages: options?.maxEnqueuedMessages ?? Infinity,
      connectionTimeout: options?.connectionTimeout === undefined ? 10_000 : options.connectionTimeout,
      stableTimeout: options?.stableTimeout ?? 3_000,
      reconnectionDelay: options?.reconnectionDelay ?? ((n) => {
        const delay = Math.min(2 ** n * 150, 10_000);
        return delay / 2 + Math.random() * (delay / 2);
      }),
      shouldReconnect: options?.shouldReconnect ?? (() => true),
    };

    // Background reconnection loop — handles its own errors via _terminate.
    this._runLoop();
  }

  // ===========================================================================
  // Reconnection lifecycle
  // ===========================================================================

  /** Run the main reconnection loop. */
  private async _runLoop(): Promise<void> {
    try {
      while (true) {
        // Clear a skip left unconsumed when the previous cycle bypassed its delay block.
        this._skipNextDelay = false;

        let closeInit: CloseEventInit;
        try {
          const [url, protocols] = await this._resolveEndpoint();
          if (this.terminationSignal.aborted) break;

          // Subscribe in the same synchronous block as the construction: a socket that
          // fails immediately dispatches its events on the very first microtask.
          this._socket = new WebSocket(url, protocols);
          this._socket.binaryType = this._binaryType;
          this._armConnectionTimeout(this._socket);
          closeInit = await this._awaitSocketLifecycle(this._socket);
        } catch (error) {
          // Failure to even create the socket (e.g. a URL factory error) counts as a failed attempt.
          if (this.terminationSignal.aborted) break;
          this.dispatchEvent(new Event("error"));
          closeInit = { code: 1006, reason: String(error), wasClean: false };
        }

        const reconnectRequested = this._reconnectRequested;
        this._reconnectRequested = false;

        // HACK (nktkas/rews#7):
        // Dispatch a freshly constructed event on `this`, never the socket's own —
        // React Native's strict EventTarget throws on a foreign event.
        const closeEvent = new CloseEvent_("close", closeInit);

        if (!reconnectRequested && !this.terminationSignal.aborted) {
          if (!this.reconnectOptions.shouldReconnect(closeEvent, this._retryCount)) {
            this._terminate("RECONNECTION_DECLINED");
          } else if (this._retryCount >= this.reconnectOptions.maxRetries) {
            this._terminate("RECONNECTION_LIMIT");
          }
        }
        this.dispatchEvent(closeEvent);
        if (this.terminationSignal.aborted) return;

        if (!reconnectRequested) {
          const retryCount = this._retryCount++;

          if (!this._skipNextDelay) {
            const delay = typeof this.reconnectOptions.reconnectionDelay === "number"
              ? this.reconnectOptions.reconnectionDelay
              : this.reconnectOptions.reconnectionDelay(retryCount);
            await this._delay(delay);
            if (this.terminationSignal.aborted) break;
          }
        }
      }
    } catch (error) {
      this._terminate("UNKNOWN_ERROR", error);
    }

    // Paths that exit the loop without a close event (terminated before the socket
    // opened, during the retry sleep, or by a policy callback throwing) synthesize
    // the final one. On the throwing path the socket's real close code/reason is lost.
    this.dispatchEvent(new CloseEvent_("close", { code: 1006, reason: "", wasClean: false }));
  }

  /** Resolve the connection URL and protocols from their providers. */
  private async _resolveEndpoint(): Promise<[string | URL, string | string[] | undefined]> {
    // A hung user factory must not outlive termination — race it against the signal.
    let onAbort!: () => void;
    const aborted = new Promise<never>((_, reject) => {
      onAbort = () => reject(this.terminationSignal.reason);

      // HACK (mysticatea/event-target-shim#32):
      // React Native's AbortController polyfill (built on event-target-shim v5) ignores
      // the { signal } listener option, so the listener is removed manually instead.
      this.terminationSignal.addEventListener("abort", onAbort, { once: true });
    });

    try {
      const url = typeof this._urlProvider === "function"
        ? await Promise.race([this._urlProvider(), aborted])
        : this._urlProvider;
      const protocols = typeof this._protocolsProvider === "function"
        ? await Promise.race([this._protocolsProvider(), aborted])
        : this._protocolsProvider;

      return [url, protocols];
    } finally {
      this.terminationSignal.removeEventListener("abort", onAbort);
    }
  }

  /**
   * Close the socket and settle its lifecycle if it does not open within the timeout.
   *
   * @param socket Socket to monitor.
   */
  private _armConnectionTimeout(socket: WebSocket): void {
    const timeout = this.reconnectOptions.connectionTimeout;
    if (timeout === null) return;

    const timer = setTimeout(() => {
      if (socket.readyState !== ReconnectingWebSocket.CONNECTING) return;

      socket.close();

      // HACK (nodejs/undici#3546):
      // Node.js <24, Bun, and React Native skip the close event for a close() during CONNECTING.
      // Settle directly; the identity check guards against a stale timer.
      if (this._socket === socket) {
        this._settleLifecycle?.({ code: 1006, reason: "", wasClean: false });
      }
    }, timeout);

    const cleanup = () => clearTimeout(timer);
    for (const type of ["open", "close", "error"] as const) {
      socket.addEventListener(type, cleanup, { once: true });
    }
  }

  /** Await the full lifecycle of the given socket until it closes. */
  private _awaitSocketLifecycle(socket: WebSocket): Promise<CloseEventInit> {
    return new Promise<CloseEventInit>((resolve) => {
      let stableTimer: ReturnType<typeof setTimeout> | undefined;

      // HACK (mysticatea/event-target-shim#32):
      // React Native <=0.78 ignores the { signal } addEventListener option,
      // so the socket listeners are removed manually.
      const settle = (init: CloseEventInit): void => {
        if (this._settleLifecycle !== settle) return; // already settled
        this._settleLifecycle = undefined;
        clearTimeout(stableTimer);
        socket.removeEventListener("open", onOpen);
        socket.removeEventListener("message", onMessage);
        socket.removeEventListener("error", onError);
        socket.removeEventListener("close", onClose);
        resolve(init);
      };
      this._settleLifecycle = settle;

      const onOpen = (): void => {
        // reconnect() called during the factory await plants a skip; clear it here, or it
        // would erase the retry delay after this connection's natural close much later.
        this._skipNextDelay = false;

        stableTimer = setTimeout(() => this._retryCount = 0, this.reconnectOptions.stableTimeout);

        this.dispatchEvent(new Event("open"));
        this._flushBuffer(socket);
      };

      const onMessage = (event: MessageEvent): void => {
        this.dispatchEvent(new MessageEvent_("message", { data: event.data, origin: event.origin }));
      };

      const onError = (event: Event): void => {
        // Forward the native diagnostics (message/error) in a fresh event of the same class —
        // the socket's own event cannot be re-dispatched. Diagnostic-free events stay plain.
        const { message, error } = event as Partial<ErrorEvent>;
        const errorEvent = message !== undefined || error !== undefined
          ? new (event.constructor as typeof ErrorEvent)("error", { message, error })
          : new Event("error");
        this.dispatchEvent(errorEvent);

        // HACK (nodejs/undici#3546):
        // Node.js <24 skips close on connection failure — settle directly.
        if (socket.readyState === ReconnectingWebSocket.CONNECTING) {
          settle({ code: 1006, reason: "", wasClean: false });
        }
      };

      const onClose = (event: CloseEvent): void => {
        settle({ code: event.code, reason: event.reason, wasClean: event.wasClean });
      };

      socket.addEventListener("open", onOpen);
      socket.addEventListener("message", onMessage);
      socket.addEventListener("error", onError);
      socket.addEventListener("close", onClose);
    });
  }

  /** Flush buffered messages; a message whose send() throws is dropped. */
  private _flushBuffer(socket: WebSocket): void {
    // An open listener may have closed or reconnected the socket.
    if (socket.readyState !== ReconnectingWebSocket.OPEN) return;

    const buffer = this._messageBuffer;
    this._messageBuffer = [];
    for (const data of buffer) {
      try {
        socket.send(data);
      } catch {
        // A send() throw on an OPEN socket is data-dependent (per the WHATWG spec only the
        // CONNECTING state throws), so a retry can never succeed — the message is dropped.
      }
    }
  }

  /** Wait between retries; cut short by reconnect() and termination. */
  private _delay(ms: number): Promise<void> {
    return new Promise((resolve) => {
      const done = () => {
        if (this._skipDelay === done) this._skipDelay = undefined;
        clearTimeout(timer);
        this.terminationSignal.removeEventListener("abort", done);
        resolve();
      };
      const timer = setTimeout(done, ms);
      this._skipDelay = done;
      this.terminationSignal.addEventListener("abort", done, { once: true });
    });
  }

  /** Close the current socket and settle its lifecycle if it was still connecting. */
  private _dropSocket(code?: number, reason?: string): void {
    const wasConnecting = this._socket?.readyState === ReconnectingWebSocket.CONNECTING;
    this._socket?.close(code, reason);

    // HACK (nodejs/undici#3546):
    // Node.js <24, Bun, and React Native skip the close event when close() is called
    // during CONNECTING — settle directly.
    if (wasConnecting) {
      this._settleLifecycle?.({ code: 1006, reason: "", wasClean: false });
    }
  }

  /** Permanently terminate the instance and clean up resources. */
  private _terminate(code: ReconnectingWebSocketErrorCode, cause?: unknown): void {
    if (this.terminationSignal.aborted) return;

    this._abortController.abort(new ReconnectingWebSocketError(code, cause));
    this._socket?.close();
    this._messageBuffer = [];
  }

  // ===========================================================================
  // WebSocket property implementations
  // ===========================================================================

  // --- Properties ------------------------------------------------------------

  /**
   * Returns the URL that was used to establish the WebSocket connection.
   *
   * ---
   *
   * `""` before the first connection for a factory.
   */
  get url(): string {
    if (this._socket) return this._socket.url;
    if (typeof this._urlProvider === "function") return "";
    try {
      return new URL(this._urlProvider).href;
    } catch {
      return String(this._urlProvider);
    }
  }

  /**
   * Returns the state of the WebSocket object's connection. It can have the values described below.
   *
   * ---
   *
   * `CLOSED` only after permanent termination; any reconnection phase reports `CONNECTING`.
   */
  get readyState(): 0 | 1 | 2 | 3 {
    if (this.terminationSignal.aborted) return ReconnectingWebSocket.CLOSED;
    return this._socket?.readyState === ReconnectingWebSocket.OPEN
      ? ReconnectingWebSocket.OPEN
      : ReconnectingWebSocket.CONNECTING;
  }

  /** Number of consecutive failed reconnection attempts; resets after a connection stays open for `stableTimeout`. */
  get retryCount(): number {
    return this._retryCount;
  }

  /**
   * Returns the number of bytes of application data (UTF-8 text and binary data) that have been queued using send() but not yet been transmitted to the network.
   *
   * If the WebSocket connection is closed, this attribute's value will only increase with each call to the send() method. (The number does not reset to zero once the connection closes.)
   *
   * ```ts
   * const ws = new WebSocket("ws://localhost:8080");
   * ws.send("Hello, world!");
   * console.log(ws.bufferedAmount); // 13
   * ```
   */
  get bufferedAmount(): number {
    const socket = this._socket;
    let total = socket && socket.readyState !== ReconnectingWebSocket.CLOSED ? socket.bufferedAmount : 0;
    for (const data of this._messageBuffer) {
      if (typeof data === "string") total += new TextEncoder().encode(data).length;
      else if (data instanceof Blob) total += data.size;
      else total += data.byteLength;
    }
    return total;
  }

  /**
   * Returns the extensions selected by the server, if any.
   *
   * WebSocket extensions add optional features negotiated during the handshake via
   * the `Sec-WebSocket-Extensions` header.
   *
   * At the time of writing, there are two registered extensions:
   *
   * - [`permessage-deflate`](https://www.rfc-editor.org/rfc/rfc7692.html): Enables per-message compression using DEFLATE.
   * - [`bbf-usp-protocol`](https://usp.technology/): Used by the Broadband Forum's User Services Platform (USP).
   *
   * See the full list at [IANA WebSocket Extensions](https://www.iana.org/assignments/websocket/websocket.xml#extension-name).
   *
   * Example:
   *
   * ```ts
   * const ws = new WebSocket("ws://localhost:8080");
   * console.log(ws.extensions); // e.g., "permessage-deflate"
   * ```
   */
  get extensions(): string {
    if (this.readyState === ReconnectingWebSocket.CONNECTING) return "";
    return this._socket?.extensions ?? "";
  }

  /**
   * Returns the subprotocol selected by the server, if any.
   * It can be used in conjunction with the array form of the constructor's second argument to perform subprotocol negotiation.
   */
  get protocol(): string {
    if (this.readyState === ReconnectingWebSocket.CONNECTING) return "";
    return this._socket?.protocol ?? "";
  }

  /**
   * Returns a string that indicates how binary data from the WebSocket object is exposed to scripts:
   *
   * Can be set, to change how binary data is returned. The default is "blob".
   *
   * ```ts
   * const ws = new WebSocket("ws://localhost:8080");
   * ws.binaryType = "arraybuffer";
   * ```
   */
  get binaryType(): BinaryType {
    return this._binaryType;
  }
  set binaryType(value: BinaryType) {
    if (value !== "blob" && value !== "arraybuffer") return;
    this._binaryType = value;
    if (this._socket) this._socket.binaryType = value;
  }

  // --- Constants -------------------------------------------------------------

  /** Connection is being established. */
  readonly CONNECTING = 0;
  /** Connection is open and ready to communicate. */
  readonly OPEN = 1;
  /** Connection is in the process of closing. */
  readonly CLOSING = 2;
  /** Connection is closed. */
  readonly CLOSED = 3;

  /** Connection is being established. */
  static readonly CONNECTING = 0;
  /** Connection is open and ready to communicate. */
  static readonly OPEN = 1;
  /** Connection is in the process of closing. */
  static readonly CLOSING = 2;
  /** Connection is closed. */
  static readonly CLOSED = 3;

  // --- Event handlers --------------------------------------------------------

  /** Attribute-style handler for `close` events. */
  get onclose(): ((this: WebSocket, ev: CloseEvent) => any) | null {
    return this._onclose;
  }
  /** Set the attribute-style handler for `close` events. */
  set onclose(handler: ((this: WebSocket, ev: CloseEvent) => any) | null) {
    this._onclose = normalizeEventHandler(handler);
    this._setAttributeListener("close", this._onclose);
  }

  /** Attribute-style handler for `error` events. */
  get onerror(): ((this: WebSocket, ev: Event) => any) | null {
    return this._onerror;
  }
  /** Set the attribute-style handler for `error` events. */
  set onerror(handler: ((this: WebSocket, ev: Event) => any) | null) {
    this._onerror = normalizeEventHandler(handler);
    this._setAttributeListener("error", this._onerror);
  }

  /** Attribute-style handler for `message` events. */
  get onmessage(): ((this: WebSocket, ev: MessageEvent) => any) | null {
    return this._onmessage;
  }
  /** Set the attribute-style handler for `message` events. */
  set onmessage(handler: ((this: WebSocket, ev: MessageEvent) => any) | null) {
    this._onmessage = normalizeEventHandler(handler);
    this._setAttributeListener("message", this._onmessage);
  }

  /** Attribute-style handler for `open` events. */
  get onopen(): ((this: WebSocket, ev: Event) => any) | null {
    return this._onopen;
  }
  /** Set the attribute-style handler for `open` events. */
  set onopen(handler: ((this: WebSocket, ev: Event) => any) | null) {
    this._onopen = normalizeEventHandler(handler);
    this._setAttributeListener("open", this._onopen);
  }

  /** Attach or detach the dispatcher for an attribute-style event handler. */
  private _setAttributeListener(type: "open" | "close" | "error" | "message", handler: object | null): void {
    const active = this._attributeListeners[type];

    if (handler && !active) {
      const dispatch = (event: Event) => {
        const handler = this[`_on${type}`];
        if (typeof handler === "function") handler.call(this, event as CloseEvent & MessageEvent);
      };
      this._attributeListeners[type] = dispatch;
      super.addEventListener(type, dispatch);
    } else if (!handler && active) {
      super.removeEventListener(type, active);
      delete this._attributeListeners[type];
    }
  }

  // --- Public methods --------------------------------------------------------

  /**
   * Closes the WebSocket connection, optionally using code as the WebSocket connection close code and reason as the WebSocket connection close reason.
   *
   * @param code Status code for the closure.
   * @param reason Human-readable reason for the closure.
   *
   * @throws {DOMException} If the code is not 1000 or in 3000-4999, or the reason is longer than 123 UTF-8 bytes.
   *
   * @example
   * ```ts ignore
   * ws.close(1000, "work complete");
   * ```
   *
   * @see https://developer.mozilla.org/en-US/docs/Web/API/WebSocket/close
   */
  close(code?: number, reason?: string): void {
    code = convertAndValidateCloseArgs(code, reason);
    this._dropSocket(code, reason);
    this._terminate("TERMINATED_BY_USER");
  }

  /**
   * Drop the current connection and reconnect immediately.
   *
   * - The closed socket does not count towards `maxRetries`.
   * - The current or next retry delay is skipped.
   * - Cannot interrupt an in-flight url/protocols factory.
   * - Does nothing once permanently terminated.
   *
   * @param code Status code for closing the current socket.
   * @param reason Human-readable reason for the closure.
   *
   * @throws {DOMException} If the code is not 1000 or in 3000-4999, or the reason is longer than 123 UTF-8 bytes.
   *
   * @example
   * ```ts ignore
   * ws.reconnect(4000, "auth token refreshed");
   * ```
   */
  reconnect(code?: number, reason?: string): void {
    code = convertAndValidateCloseArgs(code, reason);
    if (this.terminationSignal.aborted) return;

    if (this._skipDelay) {
      this._skipDelay();
      return;
    }

    if (this._settleLifecycle && this._socket && this._socket.readyState !== ReconnectingWebSocket.CLOSED) {
      this._reconnectRequested = true;
      this._dropSocket(code, reason);
      return;
    }

    // Settled but not yet sleeping: skip the upcoming delay.
    this._skipNextDelay = true;
  }

  /**
   * Transmits data using the WebSocket connection. data can be a string, a Blob, an ArrayBuffer, or an ArrayBufferView.
   *
   * If the connection is not open, the data is buffered (up to `maxEnqueuedMessages`)
   * and sent once it is established. After permanent termination, data is silently discarded.
   *
   * @param data Payload to transmit or buffer.
   *
   * @example
   * ```ts ignore
   * ws.send("hello");
   * ```
   *
   * @see https://developer.mozilla.org/en-US/docs/Web/API/WebSocket/send
   */
  send(data: string | ArrayBufferLike | Blob | ArrayBufferView): void {
    if (this._socket?.readyState === ReconnectingWebSocket.OPEN) {
      this._socket.send(data);
    } else if (
      !this.terminationSignal.aborted &&
      this._messageBuffer.length < this.reconnectOptions.maxEnqueuedMessages
    ) {
      this._messageBuffer.push(data);
    }
  }
}

// =============================================================================
// Polyfills
// =============================================================================

/**
 * `CloseEvent` for React Native, which lacks the global.
 *
 * @see https://websockets.spec.whatwg.org/#the-closeevent-interface
 */
const CloseEvent_ = globalThis.CloseEvent || class CloseEvent extends Event {
  readonly code: number;
  readonly reason: string;
  readonly wasClean: boolean;
  constructor(type: string, eventInitDict?: CloseEventInit) {
    super(type, eventInitDict);
    this.code = eventInitDict?.code ?? 0;
    this.reason = eventInitDict?.reason ?? "";
    this.wasClean = eventInitDict?.wasClean ?? false;
  }
};

/**
 * `DOMException` for React Native, which lacks the global.
 *
 * @see https://webidl.spec.whatwg.org/#idl-DOMException
 */
const DOMException_ = globalThis.DOMException || class DOMException extends Error {
  constructor(message = "", name = "Error") {
    super(message);
    this.name = name;
  }
};

/**
 * `MessageEvent` for React Native, which lacks the global.
 *
 * @see https://html.spec.whatwg.org/multipage/comms.html#messageevent
 */
const MessageEvent_ = globalThis.MessageEvent || class MessageEvent<T> extends Event {
  readonly data: T | null;
  readonly origin: string;
  readonly lastEventId: string;
  readonly source: MessageEventSource | null;
  readonly ports: ReadonlyArray<MessagePort>;
  constructor(type: string, eventInitDict?: MessageEventInit<T>) {
    super(type, eventInitDict);
    this.data = eventInitDict?.data ?? null;
    this.origin = eventInitDict?.origin ?? "";
    this.lastEventId = eventInitDict?.lastEventId ?? "";
    this.source = eventInitDict?.source ?? null;
    this.ports = eventInitDict?.ports ?? [];
  }
};

// =============================================================================
// Utilities
// =============================================================================

/** Coerce an event-handler value per WebIDL `[LegacyTreatNonObjectAsNull]`: non-objects become `null`. */
function normalizeEventHandler<T>(handler: T): T | null {
  return typeof handler === "object" || typeof handler === "function" ? handler : null;
}

/**
 * Convert the close code per WebIDL `[Clamp]`, then validate code and reason per the
 * WHATWG WebSocket specification. Returns the converted code.
 */
function convertAndValidateCloseArgs(code?: number, reason?: string): number | undefined {
  if (code !== undefined) {
    // [Clamp]: NaN -> 0, clamp to the unsigned-short range, round half to even.
    if (Number.isNaN(code)) code = 0;
    else {
      code = Math.min(Math.max(code, 0), 65535);
      const rounded = Math.round(code);
      code = code % 1 === 0.5 && rounded % 2 !== 0 ? rounded - 1 : rounded;
    }
    if (code !== 1000 && !(code >= 3000 && code <= 4999)) {
      throw new DOMException_(
        `The close code must be either 1000, or between 3000 and 4999. ${code} is neither.`,
        "InvalidAccessError",
      );
    }
  }
  if (reason !== undefined && new TextEncoder().encode(reason).length > 123) {
    throw new DOMException_("The close reason must not be greater than 123 UTF-8 bytes.", "SyntaxError");
  }
  return code;
}
